import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { UserService } from '../services/user.service';
import { map, catchError, of } from 'rxjs';

/**
 * Route guard factory that restricts access to a specific user type.
 * If the user has a different type, redirects them to their own home route
 * ('/counselors' for faculty, '/students' for student) rather than a generic error page.
 * Falls back to '/' if the user fetch fails.
 *
 * @param userType - The user type required to access the guarded route.
 */
export const roleGuard =
  (userType: 'student' | 'faculty' | 'district'): CanActivateFn =>
  () => {
    const userService: UserService = inject(UserService);
    const router: Router = inject(Router);

    if (userService.currentUser) {
      if (userService.currentUser.userType === userType) return true;
      // Wrong role — redirect to their own home
      const roleRoutes: Record<string, string> = {
        faculty: '/counselors',
        student: '/students',
        district: '/districts',
      };

      const home = roleRoutes[userService.currentUser.userType] || '/home';

      router.navigateByUrl(home);

      return false;
    }

    return userService.getCurrentUser().pipe(
      map((user) => {
        if (user.userType === userType) return true;
        const roleRoutes: Record<string, string> = {
          faculty: '/counselors',
          student: '/students',
          district: '/districts',
        };

        const home = roleRoutes[user.userType] || '/home';
        router.navigateByUrl(home);
        return false;
      }),
      catchError(() => {
        router.navigateByUrl('/');
        return of(false);
      }),
    );
  };
